import { useState, type ReactNode } from 'react';
import { Trophy } from 'lucide-react';
import { type SchoolLevel } from '@jeraisonne/db';
import { LiveSchoolTab } from './live-school-tab';
import { ClassementsTab } from './classements-tab';
import { LevelSelect } from './level-select';
import { PrivateLivesSection } from '@/features/private-live/private-lives-section';

type Tab = 'actus' | 'live' | 'classements';

/**
 * Coquille à onglets des pages Feed (ex-Actus) : Feed (flux existant, injecté
 * via `actus`) / Live School / Classements. `onLevelChange` fourni ⇒ mode
 * joueur (niveau non fixe → sélecteur de niveau, lecture seule). Absent ⇒
 * mode élève (niveau fixe, peut rejoindre les défis).
 */
export function NewsTabs({
  userId,
  level,
  onLevelChange,
  actus,
}: {
  userId: string;
  level: SchoolLevel | null;
  onLevelChange?: (level: SchoolLevel) => void;
  actus: ReactNode;
}) {
  const [tab, setTab] = useState<Tab>('actus');
  const [rankingChallengeId, setRankingChallengeId] = useState<string | null>(null);

  // Niveau fixe (élève) ⇒ on peut rejoindre. Mode joueur (sélecteur) ⇒ lecture seule.
  const canJoin = !onLevelChange && !!level;

  function seeRanking(challengeId: string) {
    setRankingChallengeId(challengeId);
    setTab('classements');
  }

  return (
    <div>
      <div className="border-ink-100 mb-5 flex gap-1 border-b">
        <TabButton active={tab === 'actus'} onClick={() => setTab('actus')}>
          Feed
        </TabButton>
        <TabButton active={tab === 'live'} onClick={() => setTab('live')}>
          Live School
        </TabButton>
        <TabButton active={tab === 'classements'} onClick={() => setTab('classements')}>
          <span className="inline-flex items-center gap-1.5">
            <Trophy className="h-4 w-4" />
            Classements
          </span>
        </TabButton>
      </div>

      {onLevelChange && tab !== 'actus' && <LevelSelect value={level} onChange={onLevelChange} />}

      {tab === 'actus' && actus}
      {tab === 'live' && <PrivateLivesSection />}
      {tab === 'live' && (
        <LiveSchoolTab
          level={level}
          userId={userId}
          canJoin={canJoin}
          canPlay={!!level}
          onSeeRanking={seeRanking}
        />
      )}
      {tab === 'classements' && (
        <ClassementsTab level={level} userId={userId} initialChallengeId={rankingChallengeId} />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px min-h-11 border-b-2 px-3 text-sm font-semibold transition-colors ${
        active
          ? 'border-accent-500 text-ink-900'
          : 'text-ink-400 hover:text-ink-700 border-transparent'
      }`}
    >
      {children}
    </button>
  );
}
